import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';

import { CloseRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

export interface AssistanceResponseFeedbackModalContentProps {
  onFeedbackSubmit: (
    comments: string,
    selectedOptions: string[],
  ) => Promise<void>;
  userFeedbackOptions?: string[];
}

const QUESTION_NOT_ANSWERED_LABEL = `Question wasn't answered`;
const ANSWER_GENERIC_LABEL = 'Answer is too generic';
const INCORRECT_DATA_SELECTED_LABEL = 'Data selected was incorrect';
const INCOMPLETE_CITATIONS_LABEL = 'Citations are incomplete';

export function AssistanceResponseFeedbackModalContent({
  onFeedbackSubmit,
  userFeedbackOptions = [
    QUESTION_NOT_ANSWERED_LABEL,
    ANSWER_GENERIC_LABEL,
    INCORRECT_DATA_SELECTED_LABEL,
    INCOMPLETE_CITATIONS_LABEL,
  ],
}: AssistanceResponseFeedbackModalContentProps) {
  const { closeModal } = useMuiModalContext();
  const [comments, setComments] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(
    new Set(),
  );
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccessNotification } = useNotificationContext();

  const shouldDisableSubmit = selectedOptions.size === 0;

  const submitFeedback = async () => {
    setIsLoading(true);
    try {
      await onFeedbackSubmit(comments, Array.from(selectedOptions));
      closeModal();
      showSuccessNotification({
        contentComponent: 'Thank you for your feedback!',
        props: { isClosable: true },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const header = (
    <Stack
      direction={'row'}
      justifyContent={'space-between'}
      alignItems={'center'}
      px={2}
      py={1}
    >
      <Typography variant="h6">Send Feedback to Enterpret team</Typography>
      <IconButton>
        <CloseRounded onClick={() => closeModal()} color="primary" />
      </IconButton>
    </Stack>
  );

  const modalContent = (
    <Stack spacing={1.5} px={2}>
      <Typography variant="body1">
        Your feedback is valuable and helps us improve. Please select all
        options that best represent it.{' '}
      </Typography>
      <Grid container justifyContent={'flex-start'} rowSpacing={2}>
        {userFeedbackOptions.map((reason) => (
          <Grid item key={reason} mr={2}>
            <Button
              variant="outlined"
              fullWidth
              sx={{
                borderRadius: 25,
                textTransform: 'none',

                borderColor: selectedOptions.has(reason)
                  ? 'primary.main'
                  : 'divider',
                color: selectedOptions.has(reason)
                  ? 'primary.main'
                  : 'text.primary',
                '&:hover': {
                  borderColor: selectedOptions.has(reason)
                    ? 'primary.dark'
                    : 'divider',
                },
              }}
              onClick={() => handleSelectOption(reason)}
            >
              <Typography variant="subtitle1">{reason}</Typography>
            </Button>
          </Grid>
        ))}
      </Grid>
      <TextField
        placeholder="Add specific details (Optional)"
        multiline
        maxRows={8}
        label=""
        value={comments}
        onChange={(eve) => setComments(eve.target.value)}
        sx={{ pt: 2, pr: 2 }}
      />
    </Stack>
  );

  const footer = (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems={'center'}
      p={2}
      spacing={1}
    >
      <Typography variant="subtitle1">
        We read each feedback shared by you
      </Typography>
      <LoadingButton
        variant="contained"
        color="primary"
        disabled={shouldDisableSubmit}
        onClick={submitFeedback}
        loading={isLoading}
      >
        Send feedback
      </LoadingButton>
    </Stack>
  );

  const handleSelectOption = (option: string) => {
    const newSet = new Set(selectedOptions);
    if (newSet.has(option)) {
      newSet.delete(option);
    } else {
      newSet.add(option);
    }
    setSelectedOptions(newSet);
  };

  return (
    <DialogContent sx={{ p: 0, width: 'sm' }}>
      {header}
      <Divider
        sx={{
          opacity: 0.12,
          mb: 3,
        }}
      />
      {modalContent}
      <Divider
        sx={{
          opacity: 0.12,
          mt: 3,
        }}
      />
      {footer}
    </DialogContent>
  );
}
